import {
  Building2,
  CalendarCheck,
  CalendarRange,
  ClipboardList,
  CreditCard,
  Dumbbell,
  FileText,
  GalleryHorizontal,
  History,
  House,
  ListChecks,
  PlayCircle,
  ScrollText,
  ShieldCheck,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { FileRouteTypes } from '@/routeTree.gen';
import type { Permissao } from './matriz';

export type Grupo = 'treino' | 'conteudo' | 'pessoas' | 'configuracoes';

/** Módulos que o super-admin liga e desliga por academia (spec 022; `modulos` do `/me`). */
export type Modulo = 'treino' | 'documentos' | 'cursos';

/** Grupos do menu, na ordem em que aparecem (spec 003, CA-01). */
export const GRUPOS: readonly { id: Grupo; rotulo: string }[] = [
  { id: 'treino', rotulo: 'Treino' },
  { id: 'conteudo', rotulo: 'Conteúdo' },
  { id: 'pessoas', rotulo: 'Pessoas' },
  { id: 'configuracoes', rotulo: 'Configurações' },
];

interface TelaDoCatalogo {
  codigo: string;
  rotulo: string;
  rota: FileRouteTypes['to'];
  /** `null`: fica no topo do menu, fora dos grupos (o Início). */
  grupo: Grupo | null;
  icone: LucideIcon;
  /**
   * Permissão da API sem a qual a tela não serve, mesmo liberada na função (ex. só owner e admin
   * mexem em funções, spec 005 CA-09). Some do menu e a guarda barra.
   */
  permissao?: Permissao;
  /** Módulo da academia a que a tela pertence: desligado, ela sai do menu e da Gestão de papéis. */
  modulo?: Modulo;
}

/**
 * Catálogo de telas do painel (spec 003). O `codigo` é o do catálogo global do backend (tabela
 * `tela`; `telasPermitidas` do `/me`). A ordem aqui é a ordem do menu.
 */
export const TELAS = [
  { codigo: 'inicio', rotulo: 'Início', rota: '/', grupo: null, icone: House },
  {
    codigo: 'exercicios.lista',
    rotulo: 'Exercícios',
    rota: '/exercicios',
    grupo: 'treino',
    modulo: 'treino',
    icone: Dumbbell,
  },
  {
    codigo: 'fichas.lista',
    rotulo: 'Fichas de treino',
    rota: '/fichas',
    grupo: 'treino',
    modulo: 'treino',
    icone: ClipboardList,
  },
  {
    codigo: 'planos.lista',
    rotulo: 'Planos de treino',
    rota: '/planos',
    grupo: 'treino',
    modulo: 'treino',
    icone: CalendarRange,
  },
  {
    codigo: 'atendimentos.lista',
    rotulo: 'Prescrições',
    rota: '/atendimentos',
    grupo: 'treino',
    modulo: 'treino',
    icone: CalendarCheck,
  },
  {
    codigo: 'registros.lista',
    rotulo: 'Treinos realizados',
    rota: '/registros',
    grupo: 'treino',
    modulo: 'treino',
    icone: History,
  },
  {
    codigo: 'documentos.lista',
    rotulo: 'Documentos',
    rota: '/documentos',
    grupo: 'conteudo',
    modulo: 'documentos',
    icone: FileText,
  },
  // Spec 019: cursos em vídeo (backend 022). O profissional vê, mas não escreve (`curso:update`).
  {
    codigo: 'cursos.lista',
    rotulo: 'Cursos',
    rota: '/cursos',
    grupo: 'conteudo',
    modulo: 'cursos',
    icone: PlayCircle,
  },
  // Spec 024: o carrossel da Home do app (backend 026). Só owner e admin.
  {
    codigo: 'banners.lista',
    rotulo: 'Banners do app',
    rota: '/banners',
    grupo: 'conteudo',
    icone: GalleryHorizontal,
    permissao: 'organization:update',
  },
  { codigo: 'alunos.lista', rotulo: 'Alunos', rota: '/alunos', grupo: 'pessoas', icone: Users },
  {
    codigo: 'equipe.lista',
    rotulo: 'Colaboradores',
    rota: '/equipe',
    grupo: 'pessoas',
    icone: UserCog,
    // Spec 006, CA-12: o profissional não vê a equipe.
    permissao: 'usuario:update',
  },
  {
    codigo: 'pagamentos.assinaturas',
    rotulo: 'Pagamentos',
    rota: '/pagamentos',
    grupo: 'pessoas',
    icone: CreditCard,
    // Spec 013, CA-20: o profissional não vê pagamentos.
    permissao: 'pagamento:read',
  },
  {
    codigo: 'cadastros.apoio',
    rotulo: 'Cadastros',
    rota: '/cadastros',
    grupo: 'configuracoes',
    modulo: 'treino',
    icone: ListChecks,
  },
  {
    codigo: 'equipe.funcoes',
    rotulo: 'Gestão de papéis',
    rota: '/equipe/funcoes',
    grupo: 'configuracoes',
    icone: ShieldCheck,
    permissao: 'usuario:update',
  },
  {
    codigo: 'empresa.dados',
    rotulo: 'Dados da empresa',
    rota: '/configuracoes/empresa',
    grupo: 'configuracoes',
    icone: Building2,
    // Spec 012, CA-03: o profissional não vê os dados da academia.
    permissao: 'organization:update',
  },
  {
    codigo: 'auditoria.lista',
    rotulo: 'Auditoria (LOG)',
    rota: '/auditoria',
    grupo: 'configuracoes',
    icone: ScrollText,
    // Spec 016, CA-03 (RN-121): só owner e admin leem a auditoria.
    permissao: 'auditoria:read',
  },
] as const satisfies readonly TelaDoCatalogo[];

export type Tela = (typeof TELAS)[number];
export type CodigoDaTela = Tela['codigo'];

const POR_CODIGO = new Map<string, Tela>(TELAS.map((t) => [t.codigo, t]));

export function telaDoCodigo(codigo: string): Tela | undefined {
  return POR_CODIGO.get(codigo);
}

/** O Início é de todos os papéis da equipe (spec 003, "Paridade"), mesmo fora de `telasPermitidas`. */
export const TELA_DE_TODOS: CodigoDaTela = 'inicio';
